import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import AlertList from "../../components/alerts/AlertList";
import AlertTypeBadge from "../../components/alerts/AlertTypeBadge";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";
import { useTranslation } from "react-i18next";
import { useSharedFilters, forEndpoint } from "../../hooks/useSharedFilters";
import { getAlertOwnerEmail, getAlertVideoToken, resolveAlertMedia } from "../../utils/alertMedia";
import { useAlertVisuals } from "../../context/AlertVisualContext";

const statusLabelKeys = {
  nueva: "platform.status.new",
  en_revision: "platform.status.reviewing",
  confirmada: "platform.status.confirmed",
  falsa_alarma: "platform.status.falseAlarm",
  resuelta: "platform.status.resolved",
};

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
};

const ProfessionalAlerts = () => {
  const { t, i18n } = useTranslation();
  const { filters, setFilter: updateFilter } = useSharedFilters("alerts");
  const [alerts, setAlerts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mediaError, setMediaError] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [notes, setNotes] = useState("");
  const [comment, setComment] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const openedAlertRef = useRef(null);
  const { clearAlert } = useAlertVisuals();
  const statusLabels = useMemo(
    () => Object.fromEntries(Object.entries(statusLabelKeys).map(([key, value]) => [key, t(value)])),
    [t]
  );
  const { branch_id, camera_id, detection_type, status, severity, critical_open, date_from, date_to, zone_name } = filters;
  const alertQuery = useMemo(() => forEndpoint({ branch_id, camera_id, detection_type, status, severity, critical_open, date_from, date_to, zone_name }, "alerts"), [
    branch_id, camera_id, detection_type, status, severity, critical_open, date_from, date_to, zone_name,
  ]);
  const formatDate = useCallback((value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(i18n.language || "es-AR");
  }, [i18n.language]);

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [branchRows, cameraPayload, alertRows, userRows] = await Promise.all([
        saasApi.getBranches(),
        saasApi.getCameras({ branch_id }),
        saasApi.getAlerts(alertQuery),
        saasApi.getUsers().catch(() => []),
      ]);
      setBranches(branchRows);
      setCameras(cameraPayload.cameras || []);
      setAlerts(alertRows);
      setUsers(userRows);

    } catch (err) {
      notify(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [branch_id, alertQuery, notify]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 5000);
    return () => clearInterval(id);
  }, [fetchData]);

  const summary = useMemo(() => ({
    total: alerts.length,
    open: alerts.filter((item) => ["nueva", "en_revision"].includes(item.status)).length,
    critical: alerts.filter((item) => ["HIGH", "CRITICAL"].includes(item.severity)).length,
    falsePositive: alerts.filter((item) => item.status === "falsa_alarma").length,
  }), [alerts]);

  const openDetails = useCallback(async (alert) => {
    try {
      const detail = await saasApi.getAlert(alert.id);
      setMediaError(false);
      setThumbnailError(false);
      const media = resolveAlertMedia(detail, getAlertOwnerEmail(), await getAlertVideoToken());
      const useStoredClip = detail.clip_id && detail.clip_storage_provider === "local";
      const playback = useStoredClip
        ? await saasApi.createClipLink(detail.clip_id, { minutes: 60 }).then((link) => link.url).catch(() => "")
        : media.videoUrl || (detail.clip_id ? await saasApi.createClipLink(detail.clip_id, { minutes: 60 }).then((link) => link.url).catch(() => "") : "");
      setSelected({ ...detail, playback_url: playback, resolved_thumbnail_url: media.thumbnailUrl });
      setNotes(detail.notes || "");
      setComment("");
      setAssignedUserId(detail.assigned_user_id || "");
      await saasApi.markAlertViewed(alert.id).catch(() => null);
    } catch (err) {
      notify(err.message, "error");
    }
  }, [notify]);

  useEffect(() => {
    const id = Number(filters.alert_id);
    if (!Number.isSafeInteger(id) || id <= 0 || openedAlertRef.current === id) return;
    openedAlertRef.current = id;
    openDetails({ id });
  }, [filters.alert_id, openDetails]);

  const downloadSelectedEvidence = async () => {
    if (!selected?.playback_url) return;
    try {
      const blob = selected.clip_id && (selected.clip_storage_provider === "local" || (!selected.storage_key && !selected.clip_url))
        ? await saasApi.downloadClip(selected.clip_id)
        : await fetch(selected.playback_url).then((response) => {
          if (!response.ok) throw new Error(t("platform.evidence.downloadError"));
          return response.blob();
        });
      if (!blob.size || (blob.type && !blob.type.startsWith("video/") && blob.type !== "application/octet-stream")) {
        throw new Error(t("platform.evidence.downloadError"));
      }
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `alerta-${selected.id}.mp4`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const shareSelectedAlert = async () => {
    if (!selected) return;
    const url = `${window.location.origin}/incidents?alert_id=${selected.id}`;
    const title = `${selected.detection_type || t("platform.common.alert")} - ${selected.camera_display_name || selected.camera_name || t("platform.common.camera")}`;
    const text = `${title}\n${formatDate(selected.occurred_at)}\n${t("platform.alerts.shareRequiresAccess")}`;
    try {
      if (navigator.share) await navigator.share({ title, text, url });
      else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        notify(t("platform.alerts.linkCopied"));
      }
    } catch (error) {
      if (error.name !== "AbortError") notify(t("platform.alerts.shareError"), "error");
    }
  };

  const updateAlert = async (status) => {
    if (!selected) return;
    try {
      await saasApi.updateAlert(selected.id, { status, notes });
      clearAlert(selected.id);
      notify(t("platform.alerts.markedAs", { status: statusLabels[status] || status }));
      setSelected(null);
      fetchData();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const saveAssignment = async () => {
    if (!selected) return;
    try {
      await saasApi.updateAlert(selected.id, { assigned_user_id: assignedUserId || null, notes });
      notify(t("platform.alerts.assigneeUpdated"));
      openDetails(selected);
      fetchData();
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const addComment = async () => {
    if (!selected || !comment.trim()) return;
    try {
      await saasApi.addAlertComment(selected.id, { comment: comment.trim() });
      setComment("");
      notify(t("platform.alerts.commentAdded"));
      openDetails(selected);
    } catch (err) {
      notify(err.message, "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box className="scene-toolbar" display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Header title={t("platform.pages.alerts.title")} subtitle={t("platform.pages.alerts.subtitle")} />
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>{t("common.refresh")}</Button>
      </Box>

      <Grid container spacing={2.5} mb={3}>
        {[
          [t("platform.alerts.total"), summary.total],
          [t("platform.alerts.open"), summary.open],
          [t("platform.alerts.critical"), summary.critical],
          [t("platform.alerts.falseAlarms"), summary.falsePositive],
        ].map(([label, value]) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <GlassCard hover={false}>
              <Typography color="text.secondary" variant="body2">{label}</Typography>
              <Typography variant="h3" mt={0.5}>{value}</Typography>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      <GlassCard hover={false} sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField select size="small" label={t("platform.common.store")} value={filters.branch_id} onChange={(e) => updateFilter("branch_id", e.target.value)} fullWidth>
              <MenuItem value="">{t("platform.common.all")}</MenuItem>
              {branches.map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select size="small" label={t("platform.common.camera")} value={filters.camera_id} onChange={(e) => updateFilter("camera_id", e.target.value)} fullWidth>
              <MenuItem value="">{t("platform.common.all")}</MenuItem>
              {cameras.map((camera) => <MenuItem key={camera.id} value={camera.id}>{camera.display_name || camera.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField size="small" label={t("platform.common.detection")} value={filters.detection_type} onChange={(e) => updateFilter("detection_type", e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField size="small" label={t("platform.common.zone")} value={filters.zone_name} onChange={(e) => updateFilter("zone_name", e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select size="small" label={t("platform.common.status")} value={filters.status} onChange={(e) => updateFilter("status", e.target.value)} fullWidth>
              <MenuItem value="">{t("platform.common.all")}</MenuItem>
              {Object.entries(statusLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select size="small" label={t("executive.severity")} value={filters.severity} onChange={(e) => updateFilter("severity", e.target.value)} fullWidth>
              <MenuItem value="">{t("platform.common.all")}</MenuItem>
              {["LOW", "MEDIUM", "HIGH", "CRITICAL", "UNKNOWN"].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" type="date" label={t("platform.common.from")} value={filters.date_from} onChange={(e) => updateFilter("date_from", e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" type="date" label={t("platform.common.to")} value={filters.date_to} onChange={(e) => updateFilter("date_to", e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
        </Grid>
      </GlassCard>
      {filters.critical_open === "1" && <Chip sx={{ mb: 2 }} label={t("executive.criticalOpen")} onDelete={() => updateFilter("critical_open", "")} />}

      <Stack spacing={1.5}>
        <AlertList key={JSON.stringify(alertQuery)} alerts={alerts} statusLabels={statusLabels} formatDate={formatDate} selectedId={selected?.id} onOpen={openDetails} />
        {!loading && alerts.length === 0 && (
          <GlassCard hover={false}><Typography color="text.secondary">{t("platform.alerts.empty")}</Typography></GlassCard>
        )}
      </Stack>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t("platform.alerts.detailTitle")}</DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} mt={1}>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <AlertTypeBadge type={selected.detection_type} icon={<CampaignOutlinedIcon />} label={selected.severity} />
                <Chip label={statusLabels[selected.status] || selected.status} variant="outlined" />
                <Chip label={selected.camera_display_name || selected.camera_name || t("platform.common.camera")} variant="outlined" />
              </Stack>
              <Typography>{selected.description || selected.title}</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">{t("platform.common.zone")}</Typography><Typography>{selected.zone_name || "-"}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Track ID</Typography><Typography>{selected.track_id || "-"}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">{t("platform.common.duration")}</Typography><Typography>{selected.duration_ms ? `${selected.duration_ms} ms` : "-"}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">{t("platform.common.contract")}</Typography><Typography>{parseMaybeJson(selected.metadata)?.contract_version || "legacy"}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">{t("platform.alerts.viewed")}</Typography><Typography>{selected.viewed_by_me_at ? formatDate(selected.viewed_by_me_at) : t("platform.common.pending")}</Typography></Grid>
                <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">{t("platform.alerts.assignee")}</Typography><Typography>{selected.assigned_username || selected.assigned_email || t("platform.common.unassigned")}</Typography></Grid>
              </Grid>
              {selected.playback_url && !mediaError && (
                <Box sx={{ bgcolor: "#000", borderRadius: "8px", overflow: "hidden" }}>
                  <video src={selected.playback_url} poster={selected.resolved_thumbnail_url || undefined} controls preload="none" onError={() => setMediaError(true)} style={{ width: "100%", maxHeight: 420, display: "block" }} />
                </Box>
              )}
              {mediaError && <Alert severity="warning">{t("platform.evidence.playbackError")}</Alert>}
              {!selected.playback_url && !selected.resolved_thumbnail_url && <Alert severity="info">{t("platform.evidence.unavailable")}</Alert>}
              {selected.resolved_thumbnail_url && !thumbnailError && (
                <Box component="img" src={selected.resolved_thumbnail_url} onError={() => setThumbnailError(true)} alt={t("platform.alertCards.evidencePhoto")} sx={{ width: "100%", maxHeight: 420, objectFit: "contain", bgcolor: "#000", borderRadius: "8px" }} />
              )}
              {thumbnailError && <Alert severity="warning">{t("platform.evidence.thumbnailError")}</Alert>}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {selected.playback_url && <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={downloadSelectedEvidence}>{t("platform.evidence.download")}</Button>}
                <Button variant="outlined" startIcon={<ShareOutlinedIcon />} onClick={shareSelectedAlert}>{t("platform.alerts.share")}</Button>
              </Stack>
              <Box component="pre" sx={{ bgcolor: (theme) => theme.custom.surface.base, color: "text.secondary", fontFamily: (theme) => theme.custom.monoFont, p: 1.5, borderRadius: 1, maxHeight: 220, overflow: "auto", fontSize: 12 }}>
                {JSON.stringify({
                  bounding_box: parseMaybeJson(selected.bounding_box),
                  metadata: parseMaybeJson(selected.metadata),
                }, null, 2)}
              </Box>
              <TextField multiline minRows={3} label={t("platform.alerts.operationalNotes")} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth />
              <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                <TextField select label={t("platform.alerts.assignee")} value={assignedUserId} onChange={(e) => setAssignedUserId(e.target.value)} fullWidth>
                  <MenuItem value="">{t("platform.common.unassigned")}</MenuItem>
                  {users.map((user) => <MenuItem key={user.id} value={user.id}>{user.username || user.email} - {user.role}</MenuItem>)}
                </TextField>
                <Button variant="outlined" onClick={saveAssignment}>{t("platform.alerts.assign")}</Button>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="h6">{t("platform.alerts.comments")}</Typography>
                {(selected.comments || []).map((item) => (
                  <Box key={item.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px", p: 1.5 }}>
                    <Typography variant="body2">{item.comment}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.username || item.email || t("platform.nav.system")} - {formatDate(item.created_at)}</Typography>
                  </Box>
                ))}
                <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                  <TextField label={t("platform.alerts.newComment")} value={comment} onChange={(e) => setComment(e.target.value)} fullWidth />
                  <Button variant="outlined" onClick={addComment} disabled={!comment.trim()}>{t("platform.alerts.comment")}</Button>
                </Stack>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setSelected(null)}>{t("common.close")}</Button>
          {selected?.status !== 'en_revision' && <Button onClick={() => updateAlert("en_revision")}>Reconocer / seguimiento</Button>}
          {['nueva', 'en_revision'].includes(selected?.status) && <Button onClick={() => updateAlert("falsa_alarma")}>{statusLabels.falsa_alarma}</Button>}
          {['nueva', 'en_revision'].includes(selected?.status) && <Button onClick={() => updateAlert("confirmada")} color="warning">{t("platform.alerts.confirm")}</Button>}
          {['nueva', 'en_revision', 'confirmada'].includes(selected?.status) && <Button onClick={() => updateAlert("resuelta")} variant="contained" startIcon={<DoneAllOutlinedIcon />}>{t("platform.alerts.resolve")}</Button>}
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfessionalAlerts;
